import { createColumnHelper } from "@tanstack/react-table";
import clsx from "clsx";

import CompanyLogo from "~/components/CompanyLogo";
import Tooltip from "~/components/Tooltip";
import { EMPTY_VALUE_PLACEHOLDER } from "~/constants/textConstants.ts";
import type {
	InvoiceIndexTableData,
} from "~/modules/billing/components/UnpaidInvoicesIndexView/unpaidInvoicesIndexTableTypes.ts";
import { formatNumberToCurrency } from "~/utils/currencyUtils.ts";
import { formatDateCompact, formatSmartDateRange } from "~/utils/dateAndTimeUtils.ts";
import { customArrayIncludesSome } from "~/utils/tanStackTableUtils.ts";

const columnHelper = createColumnHelper<InvoiceIndexTableData>();

export const processedInvoicesIndexTableColumnDefs = [
	columnHelper.accessor("companyShortName", {
		header: "Kunde",
		meta: {
			cellTextAlign: "center",
			width: "6rem",
		},
		cell: (props) => {
			const companyShortName = props.getValue();
			const rowData = props.row.original;
			const isProjectInvoice = props.row.original.isProjectInvoice;
			if (!isProjectInvoice) return EMPTY_VALUE_PLACEHOLDER;
			return <div className="flex items-center gap-x-2">
				{rowData.companyAvatarImage && <CompanyLogo image={rowData.companyAvatarImage}
															sizeClassName="size-6" />}
				<span>{companyShortName}</span>
			</div>;
		},
	}),
	columnHelper.accessor("invoiceNumber", {
		header: "Rechnungsnummer",
		meta: {
			headerTextAlign: "left",
			cellTextAlign: "left",
			width: "10rem",
		},
		cell: (props) => props.getValue() || EMPTY_VALUE_PLACEHOLDER,
		sortingFn: (rowA, rowB) => {
			const consecutiveA = rowA.original.consecutiveInvoiceNumber;
			const consecutiveB = rowB.original.consecutiveInvoiceNumber;
			const invoiceA = rowA.original.invoiceNumber;
			const invoiceB = rowB.original.invoiceNumber;

			// Handle null invoice numbers first - they go to the beginning
			if (!invoiceA && !invoiceB) return 0;
			if (!invoiceA) return -1;
			if (!invoiceB) return 1;

			// Helper function to parse invoice number parts. This is needed for the earliest versions of the invoice
			// number format, where no single consecutive number was used.
			const parseInvoiceNumber = (invoiceNumber: string) => {
				const parts = invoiceNumber.split("-");
				if (parts.length >= 4) {
					return {
						year: parseInt(parts[1]),
						project: parseInt(parts[2]),
						counter: parseInt(parts[3]),
					};
				}
				return null;
			};

			// Handle null consecutive numbers - parse invoice instead
			const hasConsecutiveA = consecutiveA !== null;
			const hasConsecutiveB = consecutiveB !== null;

			// If one has consecutive and other doesn't, non-consecutive comes first
			if (hasConsecutiveA && !hasConsecutiveB) return 1;
			if (!hasConsecutiveA && hasConsecutiveB) return -1;

			// Both have consecutive numbers
			if (hasConsecutiveA && hasConsecutiveB) {
				return consecutiveA - consecutiveB;
			}

			// Neither has consecutive numbers - parse and compare
			const parsedA = parseInvoiceNumber(invoiceA);
			const parsedB = parseInvoiceNumber(invoiceB);

			// Handle unparseable cases
			if (!parsedA && !parsedB) return 0;
			if (!parsedA) return -1;
			if (!parsedB) return 1;

			// Sort by year first, then project, then counter
			if (parsedA.year !== parsedB.year) return parsedA.year - parsedB.year;
			if (parsedA.project !== parsedB.project) return parsedA.project - parsedB.project;
			return parsedA.counter - parsedB.counter;
		},
	}),
	columnHelper.accessor("totalAmountCents", {
		header: () => <div>Rechnungsbetrag<br />(brutto)</div>,
		meta: {
			headerTextAlign: "center",
			cellTextAlign: "right",
			width: "10rem",
		},
		cell: (props) => {
			const rawValue = props.getValue();
			const formattedValue = props.getValue() ? formatNumberToCurrency(props.getValue()) + " " + props.row.original.currencySymbol : EMPTY_VALUE_PLACEHOLDER;

			return <div className={clsx(rawValue < 0 && "text-secondary-500", "text-right")}>{formattedValue}</div>
		},
		footer: (props) => {
			const total = props.table.getFilteredRowModel().rows.reduce((sum, row) => {
				return sum + (row.original.totalAmountCents || 0);
			}, 0);
			return <div className="flex w-full items-center justify-between text-sm font-bold"><span>&sum;</span><span>{formatNumberToCurrency(total)} €</span></div>;
		},
	}),
	columnHelper.accessor("deliveryStartDate", {
		header: "Leistungszeitraum",
		meta: {
			headerTextAlign: "center",
			cellTextAlign: "center",
			width: "10rem",
		},
		cell: (props) => {
			const rowData = props.row.original;
			if (!rowData.deliveryStartDate || !rowData.deliveryEndDate) return EMPTY_VALUE_PLACEHOLDER;
			return formatSmartDateRange(rowData.deliveryStartDate, rowData.deliveryEndDate, {
				month: "MMM",
				year: " yy",
			});
		},
	}),
	columnHelper.accessor("transactionDate", {
		header: "Versendet /\nEmpfangen am",
		meta: {
			headerTextAlign: "center",
			cellTextAlign: "center",
			width: "8rem",
		},
		cell: (props) => {
			const date = props.getValue();
			if (!date) return EMPTY_VALUE_PLACEHOLDER;
			return formatDateCompact(date, " yyyy");
		},
		filterFn: customArrayIncludesSome,
	}),
		columnHelper.accessor("paidAt", {
			header: "Gebucht am",
			meta: {
				headerTextAlign: "center",
				cellTextAlign: "center",
				width: "8rem",
			},
			cell: (props) => {
				const date = props.getValue();
				if (!date) return EMPTY_VALUE_PLACEHOLDER;
				return formatDateCompact(date, " yyyy");
			},
			filterFn: customArrayIncludesSome,
		}),
		columnHelper.accessor("updatedAt", {
			header: "Bearbeitet am",
			meta: {
				headerTextAlign: "center",
				cellTextAlign: "center",
				width: "8rem",
			},
			cell: (props) => {
				const date = props.getValue();
				if (!date) return EMPTY_VALUE_PLACEHOLDER;
				return formatDateCompact(date, " yyyy");
			},
		}),
	columnHelper.accessor("comments", {
		header: "Bemerkungen",
		meta: {
			headerTextAlign: "center",
			cellTextAlign: "left",
		},
		cell: (props) => {
			const comments = props.getValue();
			if (!comments) return <div className="flex justify-center">{EMPTY_VALUE_PLACEHOLDER}</div>;
			return <Tooltip text={comments}
							className="max-w-96 whitespace-pre-wrap break-words">
				<div className="truncate">{comments}</div>
			</Tooltip>;
		},
	}),

];